import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

export default function DraggableTable({ 
  data = [],           // Ang listahan ng items (transactions, users, etc.)
  columns = [],        // Ang definition ng columns (id, label, sortKey, render?)
  totalCount = 0,
  page = 0,
  rowsPerPage = 10,
  orderBy = '',
  orderDirection = 'asc',
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onColumnsReorder,    // 👈 NEW: Optional callback for when columns are reordered
  selection // optional: { selectedIds: Set, onToggle: fn, onToggleAll: fn, allSelected: bool }
}) {
  
  // Local state para sa draggable order ng columns
  const [orderedColumns, setOrderedColumns] = useState(columns);

  // Update local columns pag nagbago ang props
  useEffect(() => {
    setOrderedColumns(columns);
  }, [columns]);

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const newColumns = Array.from(orderedColumns);
    const [reorderedItem] = newColumns.splice(result.source.index, 1);
    newColumns.splice(result.destination.index, 0, reorderedItem);
    setOrderedColumns(newColumns);
    
    // Call parent's callback if provided (for page-specific localStorage handling)
    if (onColumnsReorder) {
      onColumnsReorder(newColumns);
    }
  };

  return (
    <TableContainer component={Paper}>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
      
      <DragDropContext onDragEnd={onDragEnd}>
        <Table>
          <TableHead>
            <Droppable droppableId="header-row" direction="horizontal">
              {(provided) => (
                <TableRow ref={provided.innerRef} {...provided.droppableProps}>
                  {selection ? (
                    <TableCell>
                      <input type="checkbox" checked={selection.allSelected} onChange={(e) => selection.onToggleAll(e.target.checked)} />
                    </TableCell>
                  ) : null}
                  {orderedColumns.map((column, index) => (
                    <Draggable key={column.id} draggableId={column.id} index={index}>
                      {(provided, snapshot) => (
                        <TableCell
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          onClick={() => {
                            if (column.sortable && column.sortKey && onSortChange) {
                              onSortChange(column.sortKey);
                            }
                          }}
                          style={{
                            ...provided.draggableProps.style,
                            cursor: 'grab',
                            backgroundColor: snapshot.isDragging ? '#f5f5f5' : 'inherit',
                            minWidth: 100,
                            fontWeight: 'bold',
                            userSelect: 'none'
                          }}
                        >
                          {column.label}
                          {column.sortable && orderBy === column.sortKey
                            ? (orderDirection === 'asc' ? ' ▲' : ' ▼') 
                            : ''}
                        </TableCell>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableRow>
              )}
            </Droppable>
          </TableHead>
          
          <TableBody>
            {data.map((row, rowIndex) => (
              <TableRow key={row.id || rowIndex}>
                {selection ? (
                  <TableCell>
                    <input type="checkbox" checked={selection.selectedIds.has(row.id)} onChange={() => selection.onToggle(row.id)} />
                  </TableCell>
                ) : null}
                {orderedColumns.map((column) => {
                  // Dito ang magic ng Generic:
                  // Kung may custom 'render' function sa column definition, gamitin yun.
                  // Kung wala, ipakita lang ang value gamit ang column.id.
                  
                  const cellContent = column.render 
                    ? column.render(row) 
                    : row[column.id];

                  return (
                    <TableCell key={`${row.id || rowIndex}-${column.id}`}>
                      {cellContent}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DragDropContext>
    </TableContainer>
  );
}